'use client';

import { useRef, type ReactElement } from 'react';
import { cn } from '@fshr/shared-react';
import type { ImagePrimitive, AmbientVideoPrimitive } from '@fshr/shared/types';
import Image from '../image';
import AmbientVideo from '../ambient-video';
import CaretDown from '../icon/caret_down';
import { measureStickyHeaderOffset } from '@fshr/hooks';
import { useTranslations } from '@fshr/context/i18n-context';

export type Props = {
	desktopImage?: ImagePrimitive;
	mobileImage?: ImagePrimitive;
	desktopAmbientVideo?: AmbientVideoPrimitive;
	mobileAmbientVideo?: AmbientVideoPrimitive;
};

const SCROLL_SETTLE_MS = 120;
const MAX_ALIGN_CORRECTIONS = 2;

const distanceBelowStickyHeader = (section: HTMLElement): number =>
	section.getBoundingClientRect().bottom - measureStickyHeaderOffset();

const whenScrollSettles = (callback: () => void) => {
	let timer = window.setTimeout(finish, SCROLL_SETTLE_MS);

	function onScroll() {
		window.clearTimeout(timer);
		timer = window.setTimeout(finish, SCROLL_SETTLE_MS);
	}

	function finish() {
		window.removeEventListener('scroll', onScroll);
		callback();
	}

	window.addEventListener('scroll', onScroll, { passive: true });
};

export default function PlpHero({
	desktopImage,
	mobileImage,
	desktopAmbientVideo,
	mobileAmbientVideo,
}: Props): ReactElement {
	const t = useTranslations();
	const sectionRef = useRef<HTMLElement>(null);

	const scrollToContent = () => {
		const section = sectionRef.current;
		if (!section) return;

		const alignOnceSettled = (correctionsLeft: number) =>
			whenScrollSettles(() => {
				const distance = distanceBelowStickyHeader(section);
				if (Math.abs(distance) <= 1 || correctionsLeft === 0) return;
				window.scrollBy({ top: distance, behavior: 'smooth' });
				alignOnceSettled(correctionsLeft - 1);
			});

		window.scrollBy({ top: distanceBelowStickyHeader(section), behavior: 'smooth' });
		alignOnceSettled(MAX_ALIGN_CORRECTIONS);
	};

	return (
		<section
			ref={sectionRef}
			className={cn(
				'relative isolate aspect-[3/4] w-full overflow-hidden bg-black md:aspect-auto md:h-[calc(100dvh-60px)] header:h-[calc(100dvh-121px)]'
			)}
		>
			<Image
				roughSize={'l'}
				imagePrimitive={desktopImage}
				className="absolute inset-0 hidden h-full w-full object-cover md:block"
			/>
			<Image
				roughSize={'l'}
				imagePrimitive={mobileImage ?? desktopImage}
				className="absolute inset-0 h-full w-full object-cover md:hidden"
			/>

			<AmbientVideo
				desktop={desktopAmbientVideo}
				mobile={mobileAmbientVideo}
				toggleClassName="bg-img-overlay-60"
			/>

			<button
				type="button"
				onClick={scrollToContent}
				aria-label={t('Scroll to page content')}
				className="absolute bottom-[30px] left-1/2 z-10 hidden size-[48px] lg:size-360 shrink-0 -translate-x-1/2 items-center justify-center rounded-full bg-surface-translucent backdrop-blur-[15px] focus-visible:outline-2 focus-visible:outline-offset-2 md:flex"
			>
				<CaretDown width={18} height={9} />
			</button>
		</section>
	);
}
